import { getRouteApi, Link } from "@tanstack/react-router";
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useFamilyGoalsSummary } from "creso-core/hooks";
import {
  FamilyGoalsSummaryGoal,
  FamilyGoalsSummaryMember,
  GoalCategory,
} from "creso-core/types";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
  formatGoalEta,
  getInitials,
} from "creso-core/utils";
import { ExternalLink } from "lucide-react";
import { useMemo } from "react";
import { Avatar, GoalIcon } from "web-core";
import { cn, DataTable } from "web-design-system";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { MetricValue } from "./common";

function shouldShowGoalProgress(goal: FamilyGoalsSummaryGoal) {
  return goal.target_corpus > 0;
}

function GoalProgressRow({ goal }: { goal: FamilyGoalsSummaryGoal }) {
  const progress = goal ? Math.min(100, Math.max(0, goal.progress_pct)) : 0;
  const timeRemaining = formatGoalEta(goal.eta);

  return (
    <div className="flex items-start gap-3 min-w-0">
      <GoalIcon
        category={goal.category as GoalCategory}
        className="size-8 shrink-0 [&>svg]:size-4"
      />
      <div className="flex flex-1 flex-col gap-1.5 min-w-0">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-medium">
            {formatAsCrore(goal.current_value ?? 0, 1)} /{" "}
            {formatAsCrore(Number(goal.future_value) ?? 0, 1)}
          </span>
          {timeRemaining ? (
            <span className="text-muted-foreground shrink-0">
              {timeRemaining}
            </span>
          ) : null}
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full",
              progress < 100 ? "bg-primary" : "bg-constructive"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function MemberGoalCompletionCell({
  group,
}: {
  group: FamilyGoalsSummaryMember;
}) {
  const iconsOnly = group.goals.every((goal) => !shouldShowGoalProgress(goal));

  return (
    <div className="flex flex-col gap-6 min-w-0">
      {iconsOnly ? (
        <div className="flex items-center gap-2">
          {group.goals.map((goal) => (
            <GoalIcon
              key={goal.goal_id}
              category={goal.category as GoalCategory}
              className="size-8 shrink-0 [&>svg]:size-4"
            />
          ))}
        </div>
      ) : (
        group.goals.map((goal) =>
          shouldShowGoalProgress(goal) ? (
            <GoalProgressRow key={goal.goal_id} goal={goal} />
          ) : (
            <GoalIcon
              key={goal.goal_id}
              category={goal.category as GoalCategory}
              className="size-8 shrink-0 [&>svg]:size-4"
            />
          )
        )
      )}
    </div>
  );
}

function getMemberGoalColumns(): ColumnDef<FamilyGoalsSummaryMember>[] {
  return [
    {
      header: "Member",
      accessorKey: "display_name",
      minSize: 180,
      meta: { className: "align-top" },
      cell: ({ row }) => (
        <Link
          to="/clients/$id/planning"
          params={{ id: String(row.original.client_id) }}
          className="shrink-0 no-underline!"
          aria-label={`View ${row.original.display_name} goals`}
          target="_blank"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Avatar
              fallback={getInitials(row.original.display_name)}
              size="default"
            />
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-sm truncate">
                {row.original.display_name}
              </span>
              <span className="text-xs text-muted-foreground">
                {row.original.holding_pattern &&
                JOINT_CLIENT_HOLDING_PATTERNS.includes(
                  row.original.holding_pattern
                )
                  ? `Joint`
                  : null}
              </span>
            </div>
          </div>
        </Link>
      ),
    },
    {
      id: "goal_completion",
      header: () => <span className="block w-full">Goal Completion</span>,
      minSize: 280,
      meta: { className: "align-top" },
      cell: ({ row }) => <MemberGoalCompletionCell group={row.original} />,
    },
    {
      id: "goal_amount",
      header: () => (
        <span className="block w-full text-right">Goal Amount</span>
      ),
      size: 160,
      meta: { className: "align-top text-right" },
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {convertToCurrency(row.original.total_target_corpus)}
        </span>
      ),
    },
    {
      id: "actions",
      size: 50,
      meta: { className: "align-top" },
      cell: ({ row }) =>
        row.original.client_id ? (
          <div className="flex justify-end">
            <Link
              to="/clients/$id/planning"
              params={{ id: String(row.original.client_id) }}
              className="shrink-0"
              aria-label={`View ${row.original.display_name} goals`}
              target="_blank"
            >
              <ExternalLink className="size-4" />
            </Link>
          </div>
        ) : null,
    },
  ];
}

export function FamilyGoalsSummarySection() {
  const route = getRouteApi("/(auth)/_headed/clients/families/$id");
  const { family } = route.useLoaderData();
  const { data: goalsSummary } = useFamilyGoalsSummary(family.id);

  const members = goalsSummary?.members ?? [];
  const columns = useMemo(() => getMemberGoalColumns(), []);

  const table = useReactTable({
    data: members,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.client_id),
    enableRowSelection: false,
  });

  if (!goalsSummary || !members.length) return null;

  return (
    <section className="flex flex-col gap-5 rounded-xl">
      <div className="text-base font-semibold">Goals Summary</div>

      <div className="rounded-xl bg-card p-4 flex flex-col gap-5">
        <div className="flex flex-row gap-18">
          <MetricValue
            label="Total Goal Value"
            value={formatAsCrore(goalsSummary.summary.total_goal_value ?? 0, 1)}
          />
          <MetricValue
            label="No. of goals"
            value={String(goalsSummary.summary.goal_count ?? 0)}
          />
          <MetricValue
            label="Average Completion"
            value={
              goalsSummary.summary.average_completion != null
                ? formatAsPercentage(
                    goalsSummary.summary.average_completion,
                    "-",
                    1
                  )
                : "—"
            }
          />
        </div>

        {members.length ? (
          <DataTable
            className="bg-background overflow-hidden"
            pagination={false}
            table={table}
          />
        ) : null}
      </div>
    </section>
  );
}
